import { getDashboardIndustryPerformance } from '@simplywallst/services';
import type { QueryFunctionContext } from '@tanstack/react-query';
import { withAuth } from '@utilities/queries';
import type { PrimaryCountryISO } from '@/constants/countries';
import { toPercentage } from '@/utilities/formatting';
import { ROUTE_STOCKS_HOME } from '@/constants/routes';
import { getPath } from '@/router/utils';
import { slugify } from '@/utilities/slugify';

type IndustryPerformance = Awaited<
  ReturnType<typeof getDashboardIndustryPerformance>
>['data']['data']['top']['return_7d'][number];

const formatterPercentage = (value: number | null) =>
  value !== null ? toPercentage(value, 1) : 'n/a';

interface IndustryEntity {
  id: number;
  name: string;
  slug: string;
  canonicalUrl: string;
  return7D: number | null;
  return7DFormatted: string;
  return30D: number | null;
  return30DFormatted: string;
  return90D: number | null;
  return90DFormatted: string;
  return1YrAbs: number | null;
  return1YrAbsFormatted: string;
  return3YrAbs: number | null;
  return3YrAbsFormatted: string;
  return5YrAbs: number | null;
  return5YrAbsFormatted: string;
}

const performanceMapper = (n: IndustryPerformance): IndustryEntity => ({
  id: n.id,
  name: n.industry,
  slug: slugify(n.industry),
  canonicalUrl: getPath({ type: ROUTE_STOCKS_HOME, payload: {} }), // TODO: Discuss industry URLs
  return7D: n.return_7d || null,
  return7DFormatted: formatterPercentage(n.return_7d || null),
  return30D: n.return_30d || null,
  return30DFormatted: formatterPercentage(n.return_30d || null),
  return90D: n.return_90d || null,
  return90DFormatted: formatterPercentage(n.return_90d || null),
  return1YrAbs: n.return_1yr_abs || null,
  return1YrAbsFormatted: formatterPercentage(n.return_1yr_abs || null),
  return3YrAbs: n.return_3yr_abs || null,
  return3YrAbsFormatted: formatterPercentage(n.return_3yr_abs || null),
  return5YrAbs: n.return_5yr_abs || null,
  return5YrAbsFormatted: formatterPercentage(n.return_5yr_abs || null),
});

type Period = '7D' | '1M' | '3M' | '1Y' | '3Y' | '5Y';

export interface Response {
  top: Record<Period, IndustryEntity[]>;
  worst: Record<Period, IndustryEntity[]>;
}

export const getDefault = (): Response => ({
  top: {
    '7D': [],
    '1M': [],
    '3M': [],
    '1Y': [],
    '3Y': [],
    '5Y': [],
  },
  worst: {
    '7D': [],
    '1M': [],
    '3M': [],
    '1Y': [],
    '3Y': [],
    '5Y': [],
  },
});

export default async ({
  queryKey: [_key, market],
}: QueryFunctionContext<[string, PrimaryCountryISO]>): Promise<
  Response | undefined
> => {
  try {
    const { data } = await withAuth(getDashboardIndustryPerformance, market);

    const {
      data: { top, worst },
    } = data;

    return {
      top: {
        '7D': top.return_7d.map(performanceMapper),
        '1M': top.return_30d.map(performanceMapper),
        '3M': top.return_90d.map(performanceMapper),
        '1Y': top.return_1yr_abs.map(performanceMapper),
        '3Y': top.return_3yr_abs.map(performanceMapper),
        '5Y': top.return_5yr_abs.map(performanceMapper),
      },
      worst: {
        '7D': worst.return_7d.map(performanceMapper),
        '1M': worst.return_30d.map(performanceMapper),
        '3M': worst.return_90d.map(performanceMapper),
        '1Y': worst.return_1yr_abs.map(performanceMapper),
        '3Y': worst.return_3yr_abs.map(performanceMapper),
        '5Y': worst.return_5yr_abs.map(performanceMapper),
      },
    };
  } catch (error: any) {
    console.log(error.message);
  }
};
